import { useState, useMemo, useCallback } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { useSearchWithParams, useParams as useParamsHook } from "@/hooks/useSearchParams";
import type { BackofficeEducationUniversityTestResultsInput, StudentTestResult } from "../types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import usePagination from "@/hooks/usePagination";
import { buildColumns } from "../_utils";
import { LEVEL_CODE_FILTER_OPTIONS } from "@/pages/education/universities/_shared/constants";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import SearchInput from "@/components/SearchInput";
import { GET_UNIVERSITY_TEST_RESULTS } from "../queries";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import { buildUniversityStudentDetailLink } from "../../../_shared/_utils";

interface TestResultsTableProps {
  isAlumni: boolean;
  heading: string;
  body: string;
  searchPlaceholder: string;
  testId: string;
}

export function TestResultsTable({ isAlumni, heading, body, searchPlaceholder, testId }: TestResultsTableProps) {
  const { shouldShowPersonProfileActions } = useUniversityStaffPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { universityId } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination({
    defaultPageSize: 20
  });
  const { isCompactLayout } = useIsCompactLayout();

  const [selectedStudent, setSelectedStudent] = useState<StudentTestResult | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);

  const { params, addSearchParam, deleteSearchParams } = useParamsHook();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });

  const [selectedStatus, setSelectedStatus] = useState<string[]>(params.status ? params.status.split(",") : []);
  const [selectedLevel, setSelectedLevel] = useState<string[]>(params.level ? params.level.split(",") : []);
  const [selectedRetake, setSelectedRetake] = useState<string[]>(params.isRetake ? params.isRetake.split(",") : []);
  const [selectedDateRange, setSelectedDateRange] = useState<{ from: string; to: string } | undefined>(
    params.testDateFrom && params.testDateTo ? { from: params.testDateFrom, to: params.testDateTo } : undefined
  );
  const [dateFieldValue, setDateFieldValue] = useState<{ startDate: Date | null; endDate: Date | null } | null>(
    params.testDateFrom && params.testDateTo
      ? { startDate: new Date(params.testDateFrom), endDate: new Date(params.testDateTo) }
      : null
  );

  const handleViewStudent = useCallback((testResult: StudentTestResult) => {
    setSelectedStudent(testResult);
    setIsDetailsPanelOpen(true);
  }, []);

  // Build the input for the query
  const getTestStatus = () => {
    if (selectedStatus.length === 0) return undefined;
    if (selectedStatus.length === 2) return undefined;
    return selectedStatus.includes("passed") ? "PASS" : "FAIL";
  };

  const getIsRetake = () => {
    if (selectedRetake.length === 0) return undefined;
    if (selectedRetake.length === 2) return undefined;
    return selectedRetake.includes("true");
  };

  const input: BackofficeEducationUniversityTestResultsInput = {
    universityLegacyIds: [universityId?.toString() || ""],
    alumni: isAlumni,
    search: debouncedSearch || undefined,
    levelCodes: selectedLevel.length > 0 ? selectedLevel : undefined,
    testStatus: getTestStatus(),
    testDateRange: selectedDateRange,
    isRetake: getIsRetake()
  };

  const { data, loading } = useQuery(GET_UNIVERSITY_TEST_RESULTS, {
    variables: {
      ...paginationVariables,
      input
    },
    skip: !universityId
  });

  const testResults: StudentTestResult[] = useMemo(() => {
    if (!data?.universityTestResults?.nodes) return [];

    return data.universityTestResults.nodes.map((node: any) => ({
      id: `${node.student?.id}-${node.testId}-${node.attemptNumber}`,
      testId: node.testId,
      testName: node.testDefinition?.description || node.testId,
      level: node.levelCode,
      testDate: node.testDate,
      isRetake: node.isRetake,
      score: node.score,
      maxScore: node.maxScore,
      passed: node.isPassed,
      student: {
        id: node.student?.pgaId,
        firstName: node.student?.firstName,
        lastName: node.student?.lastName,
        displayName: node.student?.displayName,
        pgaId: node.student?.pgaId,
        profilePhoto: node.student?.profilePhoto,
        universityName: node.student?.university?.name
      }
    }));
  }, [data]);

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        handleViewStudent
      }),
    [isCompactLayout, handleViewStudent]
  );

  const filterState = {
    dates: !!selectedDateRange,
    status: selectedStatus.length > 0,
    level: selectedLevel.length > 0,
    isRetake: selectedRetake.length > 0
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setSelectedStatus([]);
    setSelectedLevel([]);
    setSelectedRetake([]);
    setSelectedDateRange(undefined);
    setDateFieldValue(null);
    deleteSearchParams(["status", "level", "isRetake", "testDateFrom", "testDateTo"]);
    clearSearch();
    resetPagination();
  };

  const hasActiveFilters = searchValue || activeFilterCount > 0;

  return (
    <>
      <TableHeading heading={heading} body={body} />
      <Table
        data={testResults}
        loading={loading}
        columns={columns}
        getRowLink={
          isAlumni
            ? undefined
            : (row) => {
                const pgaId = row.original.student.pgaId;
                if (!universityId || !pgaId) {
                  return null;
                }

                return buildUniversityStudentDetailLink({
                  universityId,
                  pgaId,
                  returnPath: `${location.pathname}${location.search}`,
                  subPath: "test-results"
                });
              }
        }
        onClickRow={isAlumni ? (row) => handleViewStudent(row.original) : undefined}
        data-testid={testId}
        hasActiveFilters={hasActiveFilters}
        renderNoDataEmptyState={() => (
          <EmptyState
            title={isAlumni ? "No Alumni Test Results" : "No Test Results"}
            caption="There are no records to display at this time"
            iconName="quiz"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            title={isAlumni ? "No Matching Alumni Test Results" : "No Matching Test Results"}
            caption="Try adjusting your search or filters."
            iconName="search"
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            onSortClick={onSortClick}
            pagination={createHeaderPagination(data?.universityTestResults)}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            activeFilters={activeFilterCount}
            onClearAllFilters={handleClearAllFilters}
          >
            <SearchInput
              placeholder={searchPlaceholder}
              query={searchValue}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
            />
            <div data-name="status">
              <MultiSelect
                label="Status"
                options={[
                  { value: "passed", label: "Passed" },
                  { value: "failed", label: "Failed" }
                ]}
                selectedValues={selectedStatus}
                onSelect={(value, checked) => {
                  const updatedStatus = checked
                    ? [...selectedStatus, value]
                    : selectedStatus.filter((v) => v !== value);

                  setSelectedStatus(updatedStatus);

                  if (updatedStatus.length > 0) {
                    addSearchParam("status", updatedStatus.join(","));
                  } else {
                    deleteSearchParams("status");
                  }
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedStatus([]);
                  deleteSearchParams("status");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="level">
              <MultiSelect
                label="Level"
                options={LEVEL_CODE_FILTER_OPTIONS}
                selectedValues={selectedLevel}
                onSelect={(value, checked) => {
                  const updatedLevel = checked ? [...selectedLevel, value] : selectedLevel.filter((v) => v !== value);

                  setSelectedLevel(updatedLevel);

                  if (updatedLevel.length > 0) {
                    addSearchParam("level", updatedLevel.join(","));
                  } else {
                    deleteSearchParams("level");
                  }
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedLevel([]);
                  deleteSearchParams("level");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="isRetake">
              <MultiSelect
                label="Is Retake"
                options={[
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" }
                ]}
                selectedValues={selectedRetake}
                onSelect={(value, checked) => {
                  const updatedRetake = checked
                    ? [...selectedRetake, value]
                    : selectedRetake.filter((v) => v !== value);

                  setSelectedRetake(updatedRetake);

                  if (updatedRetake.length > 0) {
                    addSearchParam("isRetake", updatedRetake.join(","));
                  } else {
                    deleteSearchParams("isRetake");
                  }
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedRetake([]);
                  deleteSearchParams("isRetake");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="dates" data-filter-size="custom">
              <DateSelect
                key={dateFieldValue ? `date-${dateFieldValue.startDate}-${dateFieldValue.endDate}` : "date-empty"}
                label="Test Date Range"
                placeholder="Select Date Range"
                useSingleDate={false}
                useModal={true}
                value={dateFieldValue}
                onChange={(e) => {
                  if (typeof e === "object" && e !== null && e.startDate && e.endDate) {
                    const startDate =
                      e.startDate instanceof Date ? e.startDate.toISOString().split("T")[0] : e.startDate.toString();
                    const endDate =
                      e.endDate instanceof Date ? e.endDate.toISOString().split("T")[0] : e.endDate.toString();

                    setDateFieldValue({ startDate: e.startDate as Date, endDate: e.endDate as Date });
                    setSelectedDateRange({ from: startDate, to: endDate });
                    addSearchParam("testDateFrom", startDate);
                    addSearchParam("testDateTo", endDate);
                    resetPagination();
                  } else {
                    setDateFieldValue(null);
                    setSelectedDateRange(undefined);
                    deleteSearchParams(["testDateFrom", "testDateTo"]);
                    resetPagination();
                  }
                }}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.universityTestResults)} />}
      />

      <PersonSideSheet
        open={isDetailsPanelOpen && !!selectedStudent}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={selectedStudent?.student.pgaId ?? null}
        title={isAlumni ? "Alumni Details" : "Student Details"}
        showPersonalDetails={true}
        customActionButtons={
          selectedStudent
            ? [
                ...(!isAlumni
                  ? [
                      {
                        text: "View PGM Details",
                        variant: "outlined" as const,
                        onClick: () => {
                          const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                          navigate(
                            `/education/universities/${universityId}/students/${selectedStudent.student.pgaId}?returnUrl=${returnUrl}`
                          );
                          setIsDetailsPanelOpen(false);
                        }
                      }
                    ]
                  : []),
                ...(shouldShowPersonProfileActions
                  ? [
                      {
                        text: "View Full Profile",
                        variant: isAlumni ? ("outlined" as const) : ("text" as const),
                        onClick: () => {
                          window.open(`/people/${selectedStudent.student.pgaId}/person/overview`, "_blank");
                        }
                      }
                    ]
                  : [])
              ]
            : undefined
        }
      />
    </>
  );
}
